import { Box, Stack, Text } from 'braid-design-system';
import { useTranslation } from 'react-i18next';

export type LoadingProps = {
    message?: string;
    size?: number;
};

export default function Loading({ message, size = 72 }: LoadingProps) {
    const { t } = useTranslation();
    const displayMessage = message ?? t('common.loading', { defaultValue: 'Loading...' });

    return (
        <Box
            position="fixed"
            top={0}
            left={0}
            width='full'
            height='full'
            style={{
                background: 'rgba(15, 23, 42, 0.65)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                zIndex: 9999,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}
        >
            <div className="premium-loader-card">
                <Stack space="large" align="center">
                    <div className="premium-loader-wrapper" style={{ width: size, height: size }}>
                        <div className="loader-ring outer-ring" />
                        <div className="loader-ring inner-ring" />
                        <div className="loader-center-glow" />
                    </div>
                    <Text weight="strong" size="large">
                        <span className="premium-loader-text">{displayMessage}</span>
                    </Text>
                </Stack>
            </div>
            <style>
                {`
                .premium-loader-card {
                    background: rgba(30, 41, 59, 0.75);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 24px;
                    box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05);
                    padding: 48px;
                    max-width: 320px;
                    width: 90%;
                    backdrop-filter: blur(16px);
                    -webkit-backdrop-filter: blur(16px);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    animation: card-appear 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                .premium-loader-wrapper {
                    position: relative;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                .loader-ring {
                    position: absolute;
                    border-radius: 50%;
                    border: 4px solid transparent;
                }

                .outer-ring {
                    width: 100%;
                    height: 100%;
                    border-top-color: #6366f1;
                    border-bottom-color: #8b5cf6;
                    animation: spin-clockwise 1.5s cubic-bezier(0.5, 0.1, 0.5, 0.9) infinite;
                }

                .inner-ring {
                    width: calc(100% - 16px);
                    height: calc(100% - 16px);
                    border-left-color: #3b82f6;
                    border-right-color: #10b981;
                    animation: spin-counter 1.2s cubic-bezier(0.5, 0.1, 0.5, 0.9) infinite;
                }

                .loader-center-glow {
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #6366f1, #3b82f6);
                    box-shadow: 0 0 16px #6366f1;
                    animation: pulse-glow 1.5s ease-in-out infinite;
                }

                .premium-loader-text {
                    background: linear-gradient(135deg, #ffffff 40%, #cbd5e1 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    font-size: 16px;
                    letter-spacing: 0.5px;
                    animation: text-pulse 2s ease-in-out infinite;
                    text-align: center;
                    display: block;
                }

                @keyframes spin-clockwise {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @keyframes spin-counter {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(-360deg); }
                }

                @keyframes pulse-glow {
                    0%, 100% {
                        transform: scale(0.85);
                        opacity: 0.5;
                        box-shadow: 0 0 8px #6366f1;
                    }
                    50% {
                        transform: scale(1.15);
                        opacity: 1;
                        box-shadow: 0 0 20px #3b82f6;
                    }
                }

                @keyframes text-pulse {
                    0%, 100% { opacity: 0.8; }
                    50% { opacity: 1; }
                }

                @keyframes card-appear {
                    0% {
                        transform: scale(0.9);
                        opacity: 0;
                    }
                    100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                }
                `}
            </style>
        </Box>
    );
}